import { type ReactNode, useEffect, useState } from 'react';
import { GripVertical, Play, Filter } from 'lucide-react';

export type TutorialStep = 'welcome' | 'tabs' | 'filters' | 'feed' | null;

const STEPS: TutorialStep[] = ['welcome', 'feed', 'tabs', 'filters'];

const STEP_CONFIG: Record<Exclude<TutorialStep, null>, {
  text: ReactNode;
  selector: string | null;
  position: 'below' | 'above' | 'center';
}> = {
  welcome: {
    text: 'welcome-card',
    selector: null,
    position: 'center',
  },
  tabs: {
    text: 'View techniques, then drag them into the Practicing tab when you\'re ready.',
    selector: '.top-bar-row',
    position: 'below',
  },
  filters: {
    text: 'If you want to find something specific, try the filters.',
    selector: '.search-row',
    position: 'below',
  },
  feed: {
    text: 'If you\'re up for anything, enter Discovery Mode for random techniques you can swipe through.',
    selector: '.feed-banner',
    position: 'below',
  },
};

interface TutorialProps {
  visible: boolean;
  inline?: boolean;
  step?: TutorialStep;
  onDismiss: () => void;
  onStepChange?: (step: TutorialStep) => void;
}

interface PopoverPos {
  top: number;
  left: number;
  arrowDir: 'up' | 'down' | 'none';
  arrowLeft: number;
}

export default function TutorialOverlay({ visible, inline, step, onDismiss, onStepChange }: TutorialProps) {
  // Guided mode (step-by-step)
  if (step !== undefined && step !== null) {
    return <GuidedTutorial step={step} onDismiss={onDismiss} onStepChange={onStepChange} />;
  }

  // Legacy card mode (inline / simple)
  if (!visible && !inline) return null;

  const card = (
    <div
      className="tutorial-card"
      onClick={onDismiss}
      style={inline ? { position: 'static', transform: 'none', animation: 'none' } : undefined}
    >
      <h2 className="tutorial-title">Welcome to Gnosis</h2>
      <p className="tutorial-subtitle">Your grappling technique library.</p>
      <p className="tutorial-hint">Tap anywhere to start</p>
    </div>
  );

  if (inline) return card;

  return (
    <>
      <div className="tutorial-overlay" onClick={onDismiss} />
      {card}
    </>
  );
}

function GuidedTutorial({ step, onDismiss, onStepChange }: { step: TutorialStep; onDismiss: () => void; onStepChange?: (step: TutorialStep) => void }) {
  const [pos, setPos] = useState<PopoverPos | null>(null);

  const advance = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) {
      onStepChange?.(STEPS[idx + 1]);
    } else {
      onStepChange?.(null);
      onDismiss();
    }
  };

  const dismiss = () => {
    onStepChange?.(null);
    onDismiss();
  };

  useEffect(() => {
    if (!step) {
      Promise.resolve().then(() => setPos(null));
      return;
    }

    const config = STEP_CONFIG[step];
    const tooltipWidth = 280;

    if (config.position === 'center' || !config.selector) {
      Promise.resolve().then(() => setPos({
        top: window.innerHeight / 2 - 40,
        left: window.innerWidth / 2 - tooltipWidth / 2,
        arrowDir: 'none',
        arrowLeft: 0,
      }));
      return;
    }

    let highlightedEl: Element | null = null;

    const update = () => {
      if (!config.selector) return;
      const el = document.querySelector(config.selector);
      if (!el) return;

      // Lift target element above the overlay
      if (highlightedEl !== el) {
        highlightedEl?.classList.remove('tutorial-highlight');
        el.classList.add('tutorial-highlight');
        highlightedEl = el;
      }

      const rect = el.getBoundingClientRect();
      const targetCenterX = rect.left + rect.width / 2;
      const left = targetCenterX - tooltipWidth / 2;
      const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
      const arrowLeft = Math.max(16, Math.min(targetCenterX - clampedLeft, tooltipWidth - 16));

      if (config.position === 'below') {
        setPos({ top: rect.bottom + 12, left: clampedLeft, arrowDir: 'up', arrowLeft });
      } else {
        setPos({ top: rect.top - 86, left: clampedLeft, arrowDir: 'down', arrowLeft });
      }
    };

    const timeout = setTimeout(update, 50);
    const raf = requestAnimationFrame(update);
    const interval = setInterval(update, 200);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(raf);
      clearInterval(interval);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      highlightedEl?.classList.remove('tutorial-highlight');
    };
  }, [step]);

  if (!step) return null;

  if (step === 'welcome') {
    return (
      <>
        <div className="tutorial-overlay tutorial-overlay--dim" onClick={advance} />
        <div className="tutorial-card" onClick={advance}>
          <h2 className="tutorial-title">Welcome to Gnosis</h2>
          <p className="tutorial-subtitle">Your grappling technique library.</p>
          <div className="tutorial-items">
            <div className="tutorial-item">
              <Play size={16} className="tutorial-item-icon" />
              <div>
                <strong>Browse</strong>
                <span>Tap a category to see techniques and watch videos</span>
              </div>
            </div>
            <div className="tutorial-item">
              <GripVertical size={16} className="tutorial-item-icon" />
              <div>
                <strong>Track</strong>
                <span>Drag techniques to Practicing or Finished</span>
              </div>
            </div>
            <div className="tutorial-item">
              <Filter size={16} className="tutorial-item-icon" />
              <div>
                <strong>Filter</strong>
                <span>Narrow by technique or position</span>
              </div>
            </div>
          </div>
          <p className="tutorial-hint">Tap anywhere to start</p>
          <button className="popover-skip" onClick={(e) => { e.stopPropagation(); dismiss(); }}>Skip</button>
        </div>
      </>
    );
  }

  if (!pos) return null;

  const guidedSteps = STEPS.filter((s) => s !== 'welcome');
  const stepNumber = guidedSteps.indexOf(step) + 1;

  return (
    <>
      <div className="tutorial-overlay tutorial-overlay--dim" onClick={advance} />
      <div
        key={step}
        className={`popover${pos.arrowDir !== 'none' ? ` popover--arrow-${pos.arrowDir}` : ' popover--center'}`}
        style={{
          top: pos.top,
          left: pos.left,
          '--arrow-left': `${pos.arrowLeft}px`,
          cursor: 'pointer',
        } as React.CSSProperties}
        onClick={advance}
      >
        <div className="popover-step">Step {stepNumber}/{guidedSteps.length}</div>
        <div className="popover-text">{STEP_CONFIG[step].text}</div>
        <div className="popover-hint">Tap to continue</div>
      </div>
    </>
  );
}
